import React from "react";
import { App as AntdApp, Button } from "antd";

// Ogni quanto ricontrollare se è uscita una versione nuova (oltre che al ritorno sulla scheda).
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const NOTICE_KEY = "new-version-available";

// Il bundle principale di Vite ha l'hash nel nome (/assets/index-XXXX.js): se quello indicato
// dall'index.html del server è diverso da quello caricato, è uscita una versione nuova.
const ENTRY_PATTERN = /\/assets\/index-[\w-]+\.js/;

function currentEntry() {
	const script = [...document.querySelectorAll("script[type='module'][src]")]
		.find((el) => ENTRY_PATTERN.test(el.getAttribute("src") || ""));
	return script ? script.getAttribute("src").match(ENTRY_PATTERN)[0] : null;
}

/**
 * Avvisa che è disponibile una versione nuova della dashboard, senza mai ricaricare da solo:
 * l'utente ricarica quando ha salvato il suo lavoro. In sviluppo non fa nulla (Vite non usa
 * bundle con hash).
 */
export default function NewVersionNotice() {
	const { notification } = AntdApp.useApp();
	const shownRef = React.useRef(false);

	React.useEffect(() => {
		if (import.meta.env.DEV)
			return undefined;

		const loaded = currentEntry();
		if (!loaded)
			return undefined;

		let cancelled = false;

		const check = async () => {
			if (cancelled || shownRef.current || document.visibilityState !== "visible")
				return;
			try {
				const res = await fetch("/index.html", { cache: "no-store" });
				if (!res.ok)
					return;
				const latest = (await res.text()).match(ENTRY_PATTERN)?.[0];
				if (cancelled || !latest || latest === loaded)
					return;
				shownRef.current = true;
				notification.info({
					key: NOTICE_KEY,
					message: "È disponibile una nuova versione",
					description: "Salva le modifiche in corso, poi ricarica la pagina quando vuoi.",
					duration: 0,
					placement: "bottomRight",
					actions: (
						<Button type="primary" size="small" onClick={() => window.location.reload()}>
							Ricarica ora
						</Button>
					)
				});
			}
			catch {
				// Rete assente o server in riavvio: si riprova al prossimo controllo.
			}
		};

		const onVisible = () => {
			if (document.visibilityState === "visible")
				check();
		};

		const timer = window.setInterval(check, CHECK_INTERVAL_MS);
		document.addEventListener("visibilitychange", onVisible);
		window.addEventListener("focus", check);

		return () => {
			cancelled = true;
			window.clearInterval(timer);
			document.removeEventListener("visibilitychange", onVisible);
			window.removeEventListener("focus", check);
		};
	}, [notification]);

	return null;
}
